import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import logo from "../traderhub24-logo.svg";
import { useAuth } from './contexts/AuthContext'
import Login from './components/Login'
import Register from './components/Register'
import Footer from './components/Footer'
import MarketHoursTools from './components/MarketHoursTools'

function Tools({ onBack, setCurrentPage, embedded }: { onBack: () => void; setCurrentPage: (page: "home" | "changelog" | "all-trades" | "analytics" | "tools" | "strategies" | "social-trade" | "account" | "community") => void; embedded?: boolean }) {
  const navigate = useNavigate()
  const { user, signOut, loading: authLoading } = useAuth()
  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Risk/Reward & Position Size State
  const [entryPrice, setEntryPrice] = useState("")
  const [stopLoss, setStopLoss] = useState("")
  const [target1, setTarget1] = useState("")
  const [target2, setTarget2] = useState("")
  const [accountSize, setAccountSize] = useState("")
  const [riskPercent, setRiskPercent] = useState("1")
  const [rrResult, setRrResult] = useState<{
    rr1: number | null
    rr2: number | null
    suggestedUnits: number | null
    riskDollar: number | null
    unitRiskDollar: number | null
  }>({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null })

  // Win Rate Calculator State
  const [winningTrades, setWinningTrades] = useState("")
  const [losingTrades, setLosingTrades] = useState("")
  const [winRate, setWinRate] = useState<number | null>(null)

  // Profit Factor Calculator State
  const [totalProfit, setTotalProfit] = useState("")
  const [totalLoss, setTotalLoss] = useState("")
  const [profitFactor, setProfitFactor] = useState<number | null>(null)

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  // Session control - redirect to login if not authenticated (skip when embedded; layout handles auth)
  useEffect(() => {
    if (!embedded && !authLoading && !user) {
      setShowLogin(true)
    }
  }, [embedded, authLoading, user])

  // Calculate Risk/Reward & Position Size automatically
  useEffect(() => {
    const entry = parseFloat(entryPrice)
    const stop = parseFloat(stopLoss)
    const tp1 = parseFloat(target1)
    const tp2 = parseFloat(target2)
    const account = parseFloat(accountSize)
    const risk = parseFloat(riskPercent)

    // Only calculate if we have minimum required values
    if (!entry || !stop || !account || !risk || risk <= 0 || account <= 0) {
      setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null })
      return
    }

    if (entry <= 0 || stop <= 0) {
      setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null })
      return
    }

    // Determine if long or short position
    const isLong = entry > stop
    let riskAmount: number
    let reward1: number | null = null
    let reward2: number | null = null

    if (isLong) {
      // Long position: stop < entry < targets
      if (stop >= entry) {
        setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null })
        return
      }
      riskAmount = entry - stop
      if (tp1 && tp1 > entry) {
        reward1 = tp1 - entry
      }
      if (tp2 && tp2 > entry) {
        reward2 = tp2 - entry
      }
    } else {
      // Short position: stop > entry > targets
      if (stop <= entry) {
        setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null })
        return
      }
      riskAmount = stop - entry
      if (tp1 && tp1 < entry) {
        reward1 = entry - tp1
      }
      if (tp2 && tp2 < entry) {
        reward2 = entry - tp2
      }
    }

    // Calculate results
    const riskDollar = (account * risk) / 100
    const suggestedUnits = riskAmount > 0 ? riskDollar / riskAmount : null
    const unitRiskDollar = riskAmount

    const rr1 = reward1 !== null && riskAmount > 0 ? reward1 / riskAmount : null
    const rr2 = reward2 !== null && riskAmount > 0 ? reward2 / riskAmount : null

    setRrResult({
      rr1,
      rr2,
      suggestedUnits,
      riskDollar,
      unitRiskDollar,
    })
  }, [entryPrice, stopLoss, target1, target2, accountSize, riskPercent])

  // Calculate Win Rate automatically
  useEffect(() => {
    const wins = parseFloat(winningTrades)
    const losses = parseFloat(losingTrades)

    if (wins >= 0 && losses >= 0 && (wins > 0 || losses > 0)) {
      const totalTrades = wins + losses
      if (totalTrades > 0) {
        const rate = (wins / totalTrades) * 100
        setWinRate(rate)
      } else {
        setWinRate(null)
      }
    } else {
      setWinRate(null)
    }
  }, [winningTrades, losingTrades])

  // Calculate Profit Factor automatically
  useEffect(() => {
    const profit = parseFloat(totalProfit)
    const loss = parseFloat(totalLoss)

    if (profit >= 0 && loss > 0) {
      const factor = profit / loss
      setProfitFactor(factor)
    } else if (profit > 0 && (isNaN(loss) || loss === 0)) {
      // If there's profit but no loss, profit factor is infinite
      setProfitFactor(null)
    } else {
      setProfitFactor(null)
    }
  }, [totalProfit, totalLoss])

  // Reset Risk/Reward & Position Size values
  const resetRiskRewardValues = () => {
    setEntryPrice("")
    setStopLoss("")
    setTarget1("")
    setTarget2("")
    setAccountSize("")
    setRiskPercent("1")
    setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null })
  }

  // Reset Win Rate Calculator values
  const resetWinRateValues = () => {
    setWinningTrades("")
    setLosingTrades("")
    setWinRate(null)
  }

  // Reset Profit Factor Calculator values
  const resetProfitFactorValues = () => {
    setTotalProfit("")
    setTotalLoss("")
    setProfitFactor(null)
  }

  // If not authenticated, show login prompt (skip when embedded; layout handles auth)
  if (!user && !authLoading && !embedded) {
    return (
      <div className="min-h-screen bg-app-bg text-gray-900">
        {/* Header */}
        <header className="w-full flex justify-between items-center py-6 px-6 max-w-6xl mx-auto">
          <div className="flex items-center space-x-2">
            <img 
              src={logo} 
              alt="TraderHub24" 
              className="h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={onBack}
            />
          </div>
          <div className="flex items-center gap-2">
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-gray-500 text-center mb-4">Please sign in to access trading tools</p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setShowLogin(true)}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => setShowRegister(true)}
                className="rounded-full bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors"
              >
                Sign Up
              </button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <Footer currentPage="tools" onNavigate={(page) => {
          if (page === "privacy-policy") navigate("/privacy");
          else if (page === "terms-and-conditions") navigate("/terms");
          else if (page === "risk-warning") navigate("/risk-warning");
          else if (page === "documentation") navigate("/documentation");
          else if (page === "contact") navigate("/contact");
          else if (page === "home") onBack();
          else if (page === "changelog") navigate("/changelog");
        }} />

        {/* Auth Modals */}
        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            onSwitchToRegister={() => {
              setShowLogin(false)
              setShowRegister(true)
            }}
          />
        )}
        {showRegister && (
          <Register
            onClose={() => setShowRegister(false)}
            onSwitchToLogin={() => {
              setShowRegister(false)
              setShowLogin(true)
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className={embedded ? "dashboard-page-content" : "min-h-screen bg-app-bg text-gray-900"}>
      {!embedded && (
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={onBack}
          />
          <div className="flex items-center gap-2">
            {authLoading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : user ? (
              <div className="relative flex items-center gap-2">
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showMobileMenu ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    )}
                  </svg>
                </button>

                <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                    className="px-3 md:px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                  title="Account"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                    <span className="hidden md:inline">Account</span>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-app-box rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <p className="text-sm font-semibold text-gray-900">Account</p>
                        <p className="text-sm text-gray-600 mt-1 truncate">{user.email}</p>
                    </div>
                    <div className="p-2">
                      <button
                        onClick={() => {
                          navigate("/account")
                          setShowUserMenu(false)
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4a4 4 0 100 8 4 4 0 000-8zM6 20a6 6 0 0112 0" />
                        </svg>
                        Account Settings
                      </button>
                      <button
                        onClick={() => {
                          signOut();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                className="px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                title="Sign In"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Sign In
              </button>
            )}
          </div>
        </div>

        {/* Mobile Sidebar/Menu */}
        {showMobileMenu && user && (
          <div className="md:hidden fixed inset-0 z-[100] flex">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowMobileMenu(false)} />
            <div className="relative w-64 max-w-[80%] h-full bg-app-box shadow-xl flex flex-col p-4 animate-in slide-in-from-left duration-300">
              <div className="flex items-center justify-between mb-8">
                <img src={logo} alt="TraderHub24" className="h-8 w-auto" />
                <button onClick={() => setShowMobileMenu(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="space-y-2">
                {[
                  { id: "all-trades", label: "All Trades", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
                  { id: "analytics", label: "Analytics", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
                  { id: "tools", label: "Tools", icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" },
                  { id: "strategies", label: "Strategies", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
                  { id: "social-trade", label: "Social Trade", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
                  { id: "community", label: "Community", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCurrentPage(item.id as any);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      item.id === "tools" 
                        ? "bg-orange-50 text-orange-600" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </button>
                ))}
                <button
                  onClick={() => {
                    navigate('/add-trade');
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors mt-4"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Trade
                </button>
              </nav>
            </div>
          </div>
        )}
      </header>
      )}

      {(() => {
        const W = embedded ? 'div' : 'main';
        return (
        <W className={embedded ? 'w-full px-4 md:px-6 py-6' : 'max-w-6xl mx-auto px-4 md:px-6 py-6'}>
        {!embedded && (
        <section className="hidden md:flex items-center justify-center gap-4 mb-6">
          <button
            onClick={() => setCurrentPage("home")}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center"
            title="Home"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </button>
          <button
            onClick={() => navigate("/all-trades")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            All Trades
          </button>
          <button
            onClick={() => navigate("/analytics")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Analytics
          </button>
          <button
            onClick={() => navigate("/tools")}
            className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Tools
          </button>
          <button
            onClick={() => navigate("/strategies")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Strategies
          </button>
          <button
            onClick={() => navigate("/social-trade")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Social Trade
          </button>
          <button
            onClick={() => navigate("/community")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Community
          </button>
          {user && (
            <button
              onClick={() => navigate('/add-trade')}
              className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 hover:border-orange-600 transition-colors"
            >
              Add Trade
            </button>
          )}
        </section>
        )}

        <div className="mb-6 px-2">
          <h1 className="text-2xl md:text-3xl font-bold text-center md:text-left">Trading Tools</h1>
          <p className="text-sm md:text-base text-gray-600 mt-2 text-center md:text-left">Professional calculators for risk management and performance analysis</p>
        </div>

        {/* Risk Tools Grid - Two columns: Risk/Reward left, Win Rate & Profit Factor right */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Risk / Reward & Position Size */}
          <div>
            <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-lg md:text-xl font-semibold">Risk / Reward & Position Size</h2>
                <button
                  onClick={resetRiskRewardValues}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Reset values
                </button>
              </div>
              <div className="space-y-4">
                {/* First row: Account Size and Risk % */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Size</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Account Size"
                      value={accountSize}
                      onChange={(e) => setAccountSize(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Risk % per trade</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Risk % per trade"
                      value={riskPercent}
                      onChange={(e) => setRiskPercent(e.target.value)}
                    />
                  </div>
                </div>

                {/* Second row: Entry and Stop */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Entry</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Entry"
                      value={entryPrice}
                      onChange={(e) => setEntryPrice(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Stop</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Stop"
                      value={stopLoss}
                      onChange={(e) => setStopLoss(e.target.value)}
                    />
                  </div>
                </div>

                {/* Third row: Target 1 and Target 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Target 1 (TP1)</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Target 1 (TP1)"
                      value={target1}
                      onChange={(e) => setTarget1(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Target 2 (TP2)</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Target 2 (TP2)"
                      value={target2}
                      onChange={(e) => setTarget2(e.target.value)}
                    />
                  </div>
                </div>

                {/* Results */}
                {(rrResult.rr1 !== null || rrResult.rr2 !== null || rrResult.suggestedUnits !== null || rrResult.riskDollar !== null || rrResult.unitRiskDollar !== null) && (
                  <div className="mt-4 p-4 bg-orange-50 rounded-xl space-y-2">
                    {rrResult.rr1 !== null && (
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm md:text-base">RR1:</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">{rrResult.rr1.toFixed(2)}</span>
                      </div>
                    )}
                    {rrResult.rr2 !== null && (
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm md:text-base">RR2:</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">{rrResult.rr2.toFixed(2)}</span>
                      </div>
                    )}
                    {rrResult.suggestedUnits !== null && (
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm md:text-base">Suggested Units:</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">{rrResult.suggestedUnits.toFixed(4)}</span>
                      </div>
                    )}
                    {rrResult.riskDollar !== null && (
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm md:text-base">Risk $:</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">${rrResult.riskDollar.toFixed(2)}</span>
                      </div>
                    )}
                    {rrResult.unitRiskDollar !== null && (
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm md:text-base">Unit Risk $:</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">${rrResult.unitRiskDollar.toFixed(4)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Win Rate Calculator & Profit Factor Calculator */}
          <div className="space-y-6">
            {/* Win Rate Calculator */}
            <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-lg md:text-xl font-semibold">Win Rate Calculator</h2>
                <button
                  onClick={resetWinRateValues}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Reset values
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Winning Trades</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Winning Trades"
                      value={winningTrades}
                      onChange={(e) => setWinningTrades(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Losing Trades</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Losing Trades"
                      value={losingTrades}
                      onChange={(e) => setLosingTrades(e.target.value)}
                    />
                  </div>
                </div>

                {/* Result */}
                {winRate !== null && (
                  <div className="mt-4 p-4 bg-orange-50 rounded-xl text-center">
                      <p className="text-sm text-gray-600 mb-1">Win Rate</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-700">{winRate.toFixed(2)}%</p>
                    <p className="text-[10px] md:text-xs text-gray-500 mt-2 italic">
                        {parseFloat(winningTrades) || 0} winning / {parseFloat(winningTrades) + parseFloat(losingTrades) || 0} total trades
                      </p>
                  </div>
                )}
              </div>
            </div>

            {/* Profit Factor Calculator */}
            <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-lg md:text-xl font-semibold">Profit Factor Calculator</h2>
                <button
                  onClick={resetProfitFactorValues}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Reset values
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Total Profit</label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Total Profit"
                      value={totalProfit}
                      onChange={(e) => setTotalProfit(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Total Loss</label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Total Loss"
                      value={totalLoss}
                      onChange={(e) => setTotalLoss(e.target.value)}
                    />
                  </div>
                </div>

                {/* Result */}
                {profitFactor !== null && (
                  <div className="mt-4 p-4 bg-orange-50 rounded-xl text-center">
                      <p className="text-sm text-gray-600 mb-1">Profit Factor</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-700">{profitFactor.toFixed(2)}</p>
                    <p className="text-[10px] md:text-xs text-gray-500 mt-2 italic">
                        ${parseFloat(totalProfit) || 0} profit / ${parseFloat(totalLoss) || 0} loss
                      </p>
                  </div>
                )}
              </div>
            </div>

            {/* Market Hours Tools */}
            <MarketHoursTools />
          </div>
        </section>
        </W>
        );
      })()}

      <Footer currentPage="tools" onNavigate={(page) => {
        if (page === "privacy-policy") navigate("/privacy");
        else if (page === "terms-and-conditions") navigate("/terms");
        else if (page === "risk-warning") navigate("/risk-warning");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") onBack();
        else if (page === "changelog") setCurrentPage("changelog");
      }} />

      {!embedded && showLogin && (
        <Login
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false)
            setShowRegister(true)
          }}
        />
      )}

      {!embedded && showRegister && (
        <Register
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false)
            setShowLogin(true)
          }}
        />
      )}

    </div>
  )
}

export default Tools

